// ReusableTable.tsx
import * as React from 'react';
import { Table, Input, Space, Pagination } from 'antd';
import type { TableProps, TablePaginationConfig } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { getData } from '../../actions/apiCall';
import axios from 'axios';
import { getAccess } from '../../actions/auth';
import { useDispatch } from 'react-redux';
import { MasterConfigs } from '../../interfaces/entity';
import { useSelector } from 'react-redux';
const { useState, useEffect } = React;

interface ReusableTableProps<T> extends TableProps<T> {
  config: MasterConfigs;
  loaded?: () => void;
  transformResponse?: (data: any) => { data: T[]; total: number };
}

const ReusableTable = <T extends object>({
  config,
  transformResponse,
  ...restProps
}: ReusableTableProps<T>) => {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(22);
  const { refreshTable, query }: any = useSelector((app: any) => app?.app);
  const [selectedQuery, setSelectedQuery] = useState(config?.queryParams ?? {});
  const [prevQuery, setPrevQuery] = useState(config?.queryParams ?? {});


  const scrollContainerRef = React.useRef(null);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[]
  ) => { };

  useEffect(() => {
    if (restProps?.dataSource && restProps?.dataSource?.length > 0) {
      setData(restProps.dataSource as T[]);
    }
  }, [restProps?.dataSource]);

  const loadData = async (pageNumber) => {
    if (restProps?.dataSource && restProps?.dataSource?.length > 0) {
      return
    } else if (config?.api) {
      setLoading(true);
      try {
        axios.defaults.headers.common['permissions'] = JSON.stringify(
          getAccess()
        );
        // Extract filter fields from query if they exist, otherwise use query directly
        const filterFields = query?.fields || {};
        const filterConditions = query?.conditions || {};

        // Merge filter fields with selectedQuery, excluding nested fields/conditions
        const { fields, conditions, search, keys, ...restQuery } = query || {};

        // Clean up filterFields - remove undefined/null values and handle special cases
        // Note: For date fields, we need to preserve them even if they might be dayjs objects
        const cleanedFilterFields = Object.entries(filterFields).reduce((acc, [key, value]) => {
          // Check if value is a dayjs object (has format method) - convert to ISO string
          if (value && typeof value === 'object' && 'format' in value && typeof value.format === 'function') {
            acc[key] = value.format('YYYY-MM-DD');
          } else if (value !== undefined && value !== null && value !== '') {
            // Handle "converted" status - backend expects leadStatus: 'converted'
            if (key === 'leadStatus' && value === 'converted') {
              acc[key] = 'converted';
            } else {
              acc[key] = value;
            }
          }
          return acc;
        }, {});

        console.log('[TableComponent] Filter fields:', filterFields);
        console.log('[TableComponent] Cleaned filter fields:', cleanedFilterFields);
        console.log('[TableComponent] Filter conditions:', filterConditions);

        // Include search params if they exist
        const searchParams = search ? { search, keys } : {};

        // Include conditions if they exist - need to flatten for query string
        const conditionsParams = filterConditions && Object.keys(filterConditions).length > 0
          ? Object.entries(filterConditions).reduce((acc, [key, value]) => {
            acc[`conditions[${key}]`] = value;
            return acc;
          }, {} as Record<string, any>)
          : {};

        const funtionCall = getData(config.api(), {
          current: pageNumber,
          pageSize: pageSize,
          ...restQuery,
          ...cleanedFilterFields,
          ...selectedQuery,
          ...searchParams,
          ...conditionsParams
        });
        const response = await dispatch(funtionCall);
        if (response.code === 200) {
          // Handle nested data structure: response.data.data or response.data
          const responseData = response?.data?.data || response?.data;
          const responsePagination = response?.pagination || response?.data?.pagination;

          const { data: rawData, total } = transformResponse
            ? transformResponse(response)
            : {
              data: Array.isArray(responseData) ? responseData : (responseData?.data || []),
              total: responsePagination?.total || 0
            };

          setTotal(total);
          setData(config?.loadPagination ? rawData : (prevData) => {
            // Prevent duplicates by checking _id when appending data (infinite scroll mode)
            const existingIds = new Set(prevData.map((item: any) => item._id || item.id));
            const uniqueNewData = rawData.filter((item: any) => {
              const id = item._id || item.id;
              return id && !existingIds.has(id);
            });
            return [...prevData, ...uniqueNewData];
          });
          setHasMore(responsePagination?.pageSize >= pageSize);

          // Call onDataLoad callback if provided
          if (config?.onDataLoad && rawData) {
            config.onDataLoad(rawData);
          }

          // Store summary data if available (check response.summary and response.data.summary)
          // getData returns response.data, so summary should be at response.summary
          const summaryData = response?.summary || response?.data?.summary;
          if (summaryData && config?.onSummaryLoad) {
            config.onSummaryLoad(summaryData);
          } else if (config?.onSummaryLoad) {
            // Reset summary if not available
            config.onSummaryLoad(null);
          }
        }
        onLoad();
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const checkPrevAndCurrentQuery = (prevQuery, currentQuery) => {
    return JSON.stringify(prevQuery) === JSON.stringify(currentQuery)
  }

  useEffect(() => {
    if (selectedQuery && !checkPrevAndCurrentQuery(prevQuery, selectedQuery)) {
      setData([])
      setLoading(false)
      setHasMore(true)
      // Reset summary when filters change
      if (config?.onSummaryLoad) {
        config.onSummaryLoad(null);
      }
      if (page == 1) {
        loadData(1);
      } else {
        setPage(1)
      }
      setPrevQuery(selectedQuery)
    }
  }, [selectedQuery])

  useEffect(() => {
    if (config.queryParams) {
      setSelectedQuery(config.queryParams)
    }
  }, [config.queryParams])

  // Watch for API changes and reload data
  const [apiUrl, setApiUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (config?.api) {
      const currentApiUrl = config.api();
      if (currentApiUrl !== apiUrl && currentApiUrl) {
        setApiUrl(currentApiUrl);
        setData([]);
        setPage(1);
        setHasMore(true);
        setTotal(0);
        // Use setTimeout to avoid race conditions
        const timer = setTimeout(() => {
          loadData(1);
        }, 50);
        return () => clearTimeout(timer);
      }
    }
  }, [config?.api ? config.api() : null]);

  useEffect(() => {
    loadData(page);
  }, [page]);

  useEffect(() => {
    if (refreshTable) {
      setData([])
      setLoading(false)
      setHasMore(true)
      if (page == 1) {
        loadData(1);
      } else {
        setPage(1)
      }
    }
  }, [refreshTable]);

  const onLoad = () => {
    dispatch({
      type: 'REFRESH_TABLE',
      refreshTable: false,
    })
  }


  useEffect(() => {
    const scrollContainer = document.querySelector(".ant-table-body");
    scrollContainerRef.current = scrollContainer;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore && !loading) {
        setPage((prev) => prev + 1);
      }
    };

    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [hasMore, loading]);

  const handlePaginationChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  return (
    <>
      {config?.renderComponent && config?.renderComponent()}
      <Table<T>
        size='small'
        bordered={true}
        scroll={!config?.loadPagination ? { y: 685 } : undefined}
        dataSource={data}
        columns={config.getColumns()}
        loading={loading}
        onChange={handleTableChange}
        pagination={false}
        {...config.tableProps}
        {...restProps}
        rowKey={'_id'}
      />
      {config?.loadPagination && (
        <Pagination
          size='small'
          current={page}
          pageSize={pageSize}
          total={totalCount}
          onShowSizeChange={(current, size) => {
            setPageSize(size);
            setPage(1);
          }}
          pageSizeOptions={['22', '50', '100', '200']}
          onChange={handlePaginationChange}
          style={{ marginTop: '16px', textAlign: 'right' }}
        />
      )}
    </>
  );
};

export default ReusableTable;